"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreatedTimeField = void 0;
const joi_1 = __importDefault(require("joi"));
const dayjs_1 = __importDefault(require("dayjs"));
const field_types_1 = require("../../types/field_types");
const datasheet_1 = require("../datasheet");
const date_time_base_field_1 = require("./date_time_base_field");
const validate_schema_1 = require("./validate_schema");
class CreatedTimeField extends date_time_base_field_1.DateTimeBaseField {
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.CreatedTime,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return {
            datasheetId: '',
            dateFormat: field_types_1.DateFormat['YYYY/MM/DD'],
            timeFormat: field_types_1.TimeFormat['hh:mm'],
            includeTime: false,
        };
    }
    get isComputed() {
        return true;
    }
    recordEditable() {
        return false;
    }
    validateProperty() {
        return CreatedTimeField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    stdValueToCellValue() {
        return null;
    }
    getCellValue(record) {
        var _a;
        const createdAt = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.createdAt;
        return createdAt == null ? null : (0, dayjs_1.default)(createdAt).valueOf();
    }
    get openFieldProperty() {
        const { includeTime, dateFormat, timeFormat } = this.field.property;
        return {
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: field_types_1.TimeFormat[timeFormat],
            includeTime
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return CreatedTimeField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { dateFormat, timeFormat, includeTime } = openFieldProperty;
        const defaultProperty = CreatedTimeField.defaultProperty();
        return {
            datasheetId: this.field.property.datasheetId,
            dateFormat: field_types_1.DateFormat[dateFormat],
            timeFormat: timeFormat ? field_types_1.TimeFormat[timeFormat] : defaultProperty.timeFormat,
            includeTime: includeTime !== null && includeTime !== void 0 ? includeTime : defaultProperty.includeTime
        };
    }
}
exports.CreatedTimeField = CreatedTimeField;
CreatedTimeField.propertySchema = joi_1.default.object({
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    dateFormat: joi_1.default.string().allow(...(0, validate_schema_1.enumToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.string().allow(...(0, validate_schema_1.enumToArray)(field_types_1.TimeFormat)).required(),
    includeTime: joi_1.default.boolean().required(),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
CreatedTimeField.defaultDateFormat = field_types_1.DateFormat[0];
CreatedTimeField.defaultTimeFormat = field_types_1.TimeFormat[0];
CreatedTimeField.openUpdatePropertySchema = joi_1.default.object({
    dateFormat: joi_1.default.valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.DateFormat)).required(),
    timeFormat: joi_1.default.valid(...(0, validate_schema_1.enumKeyToArray)(field_types_1.TimeFormat)),
    includeTime: joi_1.default.boolean(),
    timeZone: joi_1.default.string(),
    includeTimeZone: joi_1.default.boolean(),
}).required();
//# sourceMappingURL=created_time_field.js.map